// src/components/Quiz.js — calm question runner with design-system primitives
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Clock, CheckCircle, AlertCircle, ArrowRight, ArrowLeft, X, AlertTriangle, Info
} from 'lucide-react';
import { apiService, handleApiError } from '../services/api';
import { formatTime } from '../utils/helpers';
import MarkdownRenderer from './common/MarkdownRenderer';
import { Button, Card, StageList, Breadcrumbs } from './ui';

// Difficulty → muted dot color (status token via Tailwind classes that map to it).
const DIFFICULTY_DOT = {
  easy:   'bg-status-good',
  medium: 'bg-status-warn',
  hard:   'bg-status-bad',
};

function renderSubmitOrNextLabel({ submitting, isLastQuestion }) {
  if (submitting) {
    return (
      <>
        <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
        Submitting…
      </>
    );
  }
  if (isLastQuestion) return <><CheckCircle size={14} /> Finish quiz</>;
  return <>Next <ArrowRight size={14} /></>;
}

function jumpButtonClass({ isCurrent, has }) {
  if (isCurrent) return 'bg-accent text-white';
  if (has) return 'bg-surface border border-accent-subtle text-read hover:border-accent';
  return 'bg-surface border border-line text-mutedink hover:bg-surface-muted';
}


export default function Quiz() {
  const { courseId, domain } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const quizLength = Number(searchParams.get('length')) || 5;

  const [loading, setLoading] = useState(true);
  const [progress, setProgress] = useState({ phase: 'queued' });
  // Tick a wall-clock so the loading screen shows elapsed time. Reset
  // on a fresh generate; only ticks while loading.
  const [generationStartedAt, setGenerationStartedAt] = useState(null);
  const [elapsedSec, setElapsedSec] = useState(0);
  const [quiz, setQuiz] = useState(null);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState([]);
  const [timesTaken, setTimesTaken] = useState([]);
  const [questionStartTime, setQuestionStartTime] = useState(Date.now());
  const [submitting, setSubmitting] = useState(false);
  // Submit is a single blocking call (no streaming). We drive the
  // visible workflow off a wall-clock instead — the stages advance on
  // a fixed cadence so the user sees motion regardless of how long the
  // API call takes. If the API is fast, the workflow flashes briefly
  // and we navigate; if it's slow, the user has something to watch.
  const [submitStartedAt, setSubmitStartedAt] = useState(null);
  const [submitElapsedSec, setSubmitElapsedSec] = useState(0);
  const [showAbandonDialog, setShowAbandonDialog] = useState(false);
  const [abandoning, setAbandoning] = useState(false);
  const [error, setError] = useState(null);
  // Course meta — only used by the breadcrumb so the header reads
  // "My Courses / <Course> / Quiz: <Domain>" instead of just the domain
  // label. Best-effort: failure is silent and the breadcrumb just hides
  // the missing label.
  const [courseName, setCourseName] = useState(null);

  // Load / regenerate quiz
  useEffect(() => {
    generateQuiz();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [courseId, domain, quizLength]);

  useEffect(() => {
    let alive = true;
    apiService.getCourse(courseId)
      .then((res) => { if (alive) setCourseName(res.data?.name); })
      .catch(() => {});
    return () => { alive = false; };
  }, [courseId]);

  // Tick the elapsed counter every second while loading. Stops as soon
  // as the loading flag flips off.
  useEffect(() => {
    if (!loading || !generationStartedAt) return undefined;
    const tick = () => {
      setElapsedSec(Math.floor((Date.now() - generationStartedAt) / 1000));
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [loading, generationStartedAt]);

  // Same wall-clock pattern for submission. The /quiz/evaluate call is
  // a single blocking request — we use elapsed seconds to advance the
  // visible workflow stages on a fixed cadence (grading → mastery →
  // feedback) so the screen reads as agent-doing-work instead of a
  // frozen spinner.
  useEffect(() => {
    if (!submitting || !submitStartedAt) return undefined;
    const tick = () => {
      setSubmitElapsedSec(Math.floor((Date.now() - submitStartedAt) / 1000));
    };
    tick();
    const id = setInterval(tick, 500);
    return () => clearInterval(id);
  }, [submitting, submitStartedAt]);

  const generateQuiz = async () => {
    setLoading(true);
    setError(null);
    setProgress({ phase: 'queued' });
    setGenerationStartedAt(Date.now());
    setElapsedSec(0);
    try {
      const res = await apiService.generateQuiz(
        domain,
        (p) => setProgress(p || { phase: 'queued' }),
        quizLength, courseId,
      );
      const quizData = res.data;
      setQuiz(quizData);

      const qlen = quizData.questions?.length || 0;
      setAnswers(Array.from({ length: qlen }, () => []));
      setTimesTaken(Array.from({ length: qlen }, () => 0));
      setCurrentQuestionIndex(0);
      setQuestionStartTime(Date.now());
    } catch (err) {
      setError(handleApiError(err));
    } finally {
      setLoading(false);
    }
  };

  // reset timer when question changes
  useEffect(() => {
    setQuestionStartTime(Date.now());
  }, [currentQuestionIndex]);

  // Esc to open abandon dialog
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape' && !showAbandonDialog) setShowAbandonDialog(true);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [showAbandonDialog]);

  const recordTime = () => {
    const elapsed = Math.round((Date.now() - questionStartTime) / 1000);
    setTimesTaken((prev) => {
      const next = [...prev];
      next[currentQuestionIndex] = (next[currentQuestionIndex] || 0) + elapsed;
      return next;
    });
  };

  const handleAnswerChange = (optionId, isMulti) => {
    setAnswers((prev) => {
      const next = prev.map((a) => [...(a || [])]);
      const cur = next[currentQuestionIndex] || [];
      if (isMulti) {
        const idx = cur.indexOf(optionId);
        next[currentQuestionIndex] = idx >= 0 ? cur.filter((x) => x !== optionId) : [...cur, optionId];
      } else {
        next[currentQuestionIndex] = [optionId];
      }
      return next;
    });
  };

  const handleOpenEndedChange = (text) => {
    setAnswers((prev) => {
      const next = prev.map((a) => [...(a || [])]);
      next[currentQuestionIndex] = [text];
      return next;
    });
  };

  const goToPreviousQuestion = () => {
    if (currentQuestionIndex === 0) return;
    recordTime();
    setCurrentQuestionIndex(currentQuestionIndex - 1);
  };

  const goToNextQuestion = async () => {
    recordTime();
    const isLast = currentQuestionIndex === quiz.questions.length - 1;
    if (!isLast) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
      return;
    }
    // Submit
    setSubmitting(true);
    setSubmitStartedAt(Date.now());
    setSubmitElapsedSec(0);
    try {
      const res = await apiService.evaluateQuiz(quiz.id, answers, timesTaken, courseId);
      const evalId = res.data.id || quiz.id;
      // QuizResults reads from location.state to render — without this it
      // shows the "Quiz results not found" empty state.
      navigate(`/course/${courseId}/quiz/${domain}/results/${evalId}`, {
        state: { quizResults: res.data, quiz, answers, timesTaken },
      });
    } catch (err) {
      setError(handleApiError(err));
      setSubmitting(false);
    }
  };

  const handleAbandonQuiz = async () => {
    setAbandoning(true);
    try {
      if (quiz?.id) await apiService.abandonQuiz(quiz.id, courseId);
    } catch {}
    navigate(`/course/${courseId}`);
  };

  if (loading) {
    // Map backend job.phase → 4-stage progress. The async-job worker emits:
    //   queued → picking → drafting{done,total} → polishing → done
    // For sync (cached / static) responses, we never see backend phases —
    // the call resolves before this UI is meaningful. That's fine; we show
    // the picking row briefly and the request returns.
    const phase = progress.phase || 'queued';
    const orderedPhases = ['queued', 'picking', 'drafting', 'polishing', 'done'];
    const phaseRank = (p) => Math.max(0, orderedPhases.indexOf(p));
    const stateFor = (target) => {
      const at = phaseRank(phase);
      const t = phaseRank(target);
      if (at > t) return 'done';
      if (at === t) return 'active';
      return 'pending';
    };
    const total = progress.total ?? quizLength;
    const done = progress.done ?? 0;
    // Per-stage detail lines. Quiet, single line — what's actually
    // happening right now. Picking and polishing have no progress
    // numbers, so the detail is the only "we're alive" signal there.
    const pickDetail = phase === 'picking'
      ? `Reading your mastery in ${domain}…`
      : undefined;
    let draftDetail;
    if (phase === 'drafting') {
      draftDetail = done < total
        ? `Question ${Math.min(done + 1, total)} of ${total}`
        : 'Finishing the last questions…';
    }
    const polishDetail = phase === 'polishing'
      ? 'Tightening stems and shuffling distractors…'
      : undefined;
    const stages = [
      { id: 'pick',   label: 'Picking topics',     state: stateFor('picking'),
        detail: pickDetail },
      { id: 'draft',  label: 'Drafting questions', state: stateFor('drafting'),
        progress: phase === 'drafting' ? { done, total } : undefined,
        detail: draftDetail },
      { id: 'polish', label: 'Polishing',          state: stateFor('polishing'),
        detail: polishDetail },
    ];
    return (
      <div className="min-h-screen bg-surface-muted flex items-center justify-center px-6">
        <Card className="w-full max-w-md p-6 space-y-4">
          <h2 className="text-base font-medium text-ink">Generating your quiz</h2>
          <StageList stages={stages} elapsedSec={elapsedSec} />
        </Card>
      </div>
    );
  }

  // Submission workflow — same visual language as generation, but
  // driven by a wall-clock since /quiz/evaluate is a single blocking
  // call (no streaming progress events). Stages advance on a fixed
  // cadence so the user sees motion regardless of how long the API
  // takes; if the API resolves before the last stage, we navigate
  // immediately and the screen disappears mid-animation. Better than
  // a frozen submit button.
  if (submitting) {
    const e = submitElapsedSec;
    let submitPhase = 'feedback';
    if (e < 2) submitPhase = 'grading';
    else if (e < 5) submitPhase = 'mastery';
    const stateFor = (target) => {
      const order = ['grading', 'mastery', 'feedback'];
      const at = order.indexOf(submitPhase);
      const t = order.indexOf(target);
      if (at > t) return 'done';
      if (at === t) return 'active';
      return 'pending';
    };
    const submitStages = [
      {
        id: 'grading',
        label: 'Grading your answers',
        state: stateFor('grading'),
        detail: submitPhase === 'grading' ? `Comparing against the answer key…` : undefined,
      },
      {
        id: 'mastery',
        label: 'Updating mastery scores',
        state: stateFor('mastery'),
        detail: submitPhase === 'mastery' ? `Folding this session into the per-concept history…` : undefined,
      },
      {
        id: 'feedback',
        label: 'Building your feedback',
        state: stateFor('feedback'),
        detail: submitPhase === 'feedback' ? `Surfacing what to drill next…` : undefined,
      },
    ];
    return (
      <div className="min-h-screen bg-surface-muted flex items-center justify-center px-6">
        <Card className="w-full max-w-md p-6 space-y-4">
          <h2 className="text-base font-medium text-ink">Evaluating your quiz</h2>
          <StageList stages={submitStages} elapsedSec={submitElapsedSec} />
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-surface-muted flex items-center justify-center px-6">
        <div className="text-center space-y-4 max-w-md">
          <AlertTriangle className="w-10 h-10 text-status-warn mx-auto" />
          <p className="text-read">{error}</p>
          <div className="flex gap-2 justify-center">
            <Button variant="secondary" onClick={() => navigate(-1)}>Go back</Button>
            <Button variant="primary" onClick={generateQuiz}>Try again</Button>
          </div>
        </div>
      </div>
    );
  }

  if (!quiz?.questions || quiz.questions.length === 0) {
    return (
      <div className="min-h-screen bg-surface-muted flex items-center justify-center px-6">
        <div className="text-center space-y-4 max-w-md">
          <p className="text-read">No questions in this quiz.</p>
          <Button variant="ghost" onClick={() => navigate(-1)}>Go back</Button>
        </div>
      </div>
    );
  }

  const currentQuestion = quiz.questions[currentQuestionIndex];
  const isMultipleChoice = currentQuestion.question_type === 'multiple_choice';
  const hasNoOptions = !currentQuestion.options || currentQuestion.options.length === 0;
  const isOpenEnded = currentQuestion.question_type === 'open_ended' || hasNoOptions;
  const currentAnswer = answers[currentQuestionIndex] || [];
  const hasAnswer = isOpenEnded
    ? (currentAnswer[0] || '').trim().length > 0
    : currentAnswer.length > 0;
  const answeredCount = answers.filter((a) => (a || []).length > 0).length;
  const isLastQuestion = currentQuestionIndex === quiz.questions.length - 1;
  const progressPct = (currentQuestionIndex + 1) / quiz.questions.length;

  const domainLabel = domain?.replaceAll('_', ' ').replace(/\b\w/g, (l) => l.toUpperCase());

  return (
    <div className="min-h-screen bg-surface-muted">
      <div className="max-w-3xl mx-auto px-4 py-6 space-y-5">

        {/* Header — breadcrumb + exit, with progress ring on the right.
            Question position moved to a quiet line under the breadcrumb. */}
        <div className="space-y-1.5">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-2 min-w-0">
              <button
                onClick={() => setShowAbandonDialog(true)}
                className="p-2 -ml-2 hover:bg-surface-subtle rounded-lg transition-colors text-mutedink flex-shrink-0"
                title="Exit quiz (Esc)"
                type="button"
              >
                <X size={18} />
              </button>
              <Breadcrumbs items={[
                { label: 'My Courses', to: '/my-courses' },
                { label: courseName, to: `/course/${courseId}` },
                { label: `Quiz: ${domainLabel}` },
              ]} />
            </div>

            {/* Compact progress ring */}
            <div className="relative w-10 h-10 flex-shrink-0">
              <svg className="transform -rotate-90 w-10 h-10">
                <circle cx="20" cy="20" r="16" stroke="currentColor" strokeWidth="3" fill="none" className="text-line-subtle" />
                <circle
                  cx="20" cy="20" r="16"
                  stroke="currentColor" strokeWidth="3" fill="none"
                  strokeDasharray={`${2 * Math.PI * 16}`}
                  strokeDashoffset={`${2 * Math.PI * 16 * (1 - progressPct)}`}
                  className="text-accent transition-all duration-300"
                  strokeLinecap="round"
                />
              </svg>
              <div className="absolute inset-0 flex items-center justify-center">
                <span className="text-[10px] font-medium text-read tabular-nums">{Math.round(progressPct * 100)}%</span>
              </div>
            </div>
          </div>
          <p className="text-xs text-mutedink pl-9">
            Question {currentQuestionIndex + 1} of {quiz.questions.length}
            <span className="text-faint mx-1.5">·</span>
            {answeredCount}/{quiz.questions.length} answered
          </p>
        </div>

        {/* Scenario — calm panel */}
        {quiz.quiz_theme && (
          <Card className="p-4 flex items-start gap-3">
            <Info size={18} className="text-faint mt-0.5 flex-shrink-0" />
            <div>
              <h3 className="text-xs uppercase tracking-wide text-faint font-normal mb-1">Scenario</h3>
              <p className="text-sm text-read leading-relaxed">{quiz.quiz_theme}</p>
            </div>
          </Card>
        )}

        {/* Question card */}
        <Card>
          <div className="px-6 py-3 border-b border-line flex items-center justify-between text-xs">
            <div className="flex items-center gap-2">
              <span className="font-mono text-[10px] uppercase tracking-[0.14em] text-mutedink">
                Quiz · {currentQuestionIndex + 1} of {quiz.questions.length}
              </span>
              <span className="text-faint">·</span>
              <span className="font-mono text-[9.5px] uppercase tracking-[0.06em] px-2 py-0.5 rounded border border-line bg-surface-subtle text-mutedink">
                {currentQuestion.difficulty}
              </span>
            </div>
            <div className="flex items-center gap-1 text-mutedink">
              <Clock size={12} />
              <span className="tabular-nums">{formatTime(currentQuestion.time_limit)}</span>
            </div>
          </div>

          <div className="p-6">
            <div className="mb-6">
              <MarkdownRenderer content={currentQuestion.question_text} className="q-prompt-md max-w-none" />
            </div>

            {isMultipleChoice && (
              <div className="flex items-center gap-2 mb-5 px-3 py-2 bg-surface-muted border border-line rounded-md">
                <AlertCircle size={14} className="text-status-warn flex-shrink-0" />
                <p className="text-xs text-read">Select all that apply</p>
              </div>
            )}

            {isOpenEnded ? (
              <div className="space-y-2">
                <label className="block text-sm font-medium text-read">Your answer</label>
                <textarea
                  value={currentAnswer[0] || ''}
                  onChange={(e) => handleOpenEndedChange(e.target.value)}
                  placeholder="Type your answer. Be specific — explain your reasoning, reference commands or config as appropriate."
                  rows={8}
                  className="w-full px-4 py-3 border border-line-strong rounded-lg focus:ring-2 focus:ring-accent focus:border-transparent font-mono text-sm leading-relaxed"
                />
                {currentQuestion.expected_answer && (
                  <p className="text-xs text-mutedink">
                    Tip: a strong answer covers the key points and explains the why.
                  </p>
                )}
              </div>
            ) : (
              <div className="space-y-2">
                {(currentQuestion.options || []).map((option) => {
                  const selected = (answers[currentQuestionIndex] || []).includes(option.option_id);
                  return (
                    <button
                      key={option.option_id}
                      type="button"
                      onClick={() => handleAnswerChange(option.option_id, isMultipleChoice)}
                      className="q-opt"
                      data-state={selected ? 'selected' : undefined}
                      aria-pressed={selected}
                    >
                      <span className="q-key">{option.option_id}</span>
                      <MarkdownRenderer content={option.text} className="prose prose-sm max-w-none flex-1" />
                    </button>
                  );
                })}
              </div>
            )}

            {Array.isArray(currentQuestion.concepts_tested) && currentQuestion.concepts_tested.length > 0 && (
              <div className="q-tested">
                {currentQuestion.concepts_tested.map((c) => (
                  <span key={c} className="q-tag">{c}</span>
                ))}
              </div>
            )}
          </div>

          <div className="px-6 py-3 border-t border-line bg-surface-muted flex items-center justify-between rounded-b-xl">
            <Button
              variant="secondary"
              onClick={goToPreviousQuestion}
              disabled={currentQuestionIndex === 0}
            >
              <ArrowLeft size={14} /> Previous
            </Button>

            <Button
              variant="primary"
              onClick={goToNextQuestion}
              disabled={!hasAnswer || submitting}
            >
              {renderSubmitOrNextLabel({ submitting, isLastQuestion })}
            </Button>
          </div>
        </Card>

        {/* Quick navigation — neutral grid, accent only on current */}
        <div className="space-y-2">
          <h3 className="text-xs uppercase tracking-wide text-faint font-normal">Jump to</h3>
          <div className="grid grid-cols-5 sm:grid-cols-10 gap-2">
            {quiz.questions.map((_, idx) => {
              const has = (answers[idx] || []).length > 0;
              const isCurrent = idx === currentQuestionIndex;
              const navClass = jumpButtonClass({ isCurrent, has });
              return (
                <button
                  key={idx}
                  type="button"
                  onClick={() => setCurrentQuestionIndex(idx)}
                  className={`relative h-9 rounded-md text-sm font-medium transition-colors ${navClass}`}
                >
                  {idx + 1}
                  {has && !isCurrent && (
                    <span className="absolute -top-1 -right-1 w-2 h-2 rounded-full bg-accent" />
                  )}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Abandon dialog */}
      {showAbandonDialog && (
        <>
          <button
            type="button"
            aria-label="Close dialog"
            className="fixed inset-0 bg-black bg-opacity-40 z-40 cursor-default"
            onClick={() => setShowAbandonDialog(false)}
          />
          <div className="fixed inset-0 flex items-center justify-center z-50 p-4">
            <Card className="max-w-md w-full p-6 space-y-5">
              <div>
                <h3 className="text-lg font-semibold text-ink mb-1">Exit quiz?</h3>
                <p className="text-sm text-mutedink leading-relaxed">
                  Your progress will be lost and you'll need to start over.
                </p>
              </div>
              <div className="flex items-center gap-3">
                <Button variant="secondary" onClick={() => setShowAbandonDialog(false)} className="flex-1">
                  Continue quiz
                </Button>
                <Button variant="danger" onClick={handleAbandonQuiz} disabled={abandoning} className="flex-1">
                  {abandoning ? (
                    <><div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" /> Exiting…</>
                  ) : (
                    'Exit quiz'
                  )}
                </Button>
              </div>
            </Card>
          </div>
        </>
      )}
    </div>
  );
}
